import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, Shield, Trash2, ToggleLeft, ToggleRight, Settings2, UserPlus, Upload, Download, FileText, Pencil, Search, History } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { EmptyState } from "../components/shared/EmptyState";

const STATIC_FEATURE_LABELS = {
  blueprints: "Blueprints",
  machines: "All Machines",
  machine_index: "Machine Index",
  work_orders: "Work Orders",
  maintenance: "Maintenance",
  maintenance_calendar: "Maint. Calendar",
  network_scan: "Network Scan",
  parts_catalog: "Parts Catalog",
  qr_scanner: "QR Scanner",
};

const ROLE_LABELS = {
  admin: "Administrator/IT",
  superintendent: "Superintendent",
  technician: "Technician",
  hourly: "Hourly",
};

export default function UserManagementPage({ onLogout, userRole, isFeatureEnabled, machineCategories = [] }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggles, setToggles] = useState({});
  const [togglesLoading, setTogglesLoading] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  const [customAccess, setCustomAccess] = useState({});
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ name: "", employee_number: "", role: "hourly", rfid_card: "" });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", employee_number: "", rfid_card: "" });
  const [editLoading, setEditLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activityLog, setActivityLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // Build dynamic feature labels from categories
  const FEATURE_LABELS = { ...STATIC_FEATURE_LABELS };
  machineCategories.forEach((cat) => {
    FEATURE_LABELS[`cat_${cat.name}`] = cat.label;
  });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) toast.error("Admin access required");
      else toast.error("Failed to load users");
    } finally { setLoading(false); }
  }, []);

  const fetchToggles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/feature-toggles`, { headers: getAuthHeaders() });
      setToggles(response.data.toggles || {});
    } catch { /* toggles may not exist yet */ }
  }, []);

  useEffect(() => { fetchUsers(); fetchToggles(); }, [fetchUsers, fetchToggles]);

  const fetchActivityLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/audit-log?limit=50&search=user`, { headers: getAuthHeaders() });
      const userActions = (resp.data.entries || []).filter(e =>
        ["user_created", "user_updated", "user_deleted", "role_changed", "users_bulk_imported", "users_exported", "user_registered", "user_access_updated"].includes(e.action)
      );
      setActivityLog(userActions.slice(0, 25));
    } catch { /* audit log may not be accessible */ }
    finally { setLogLoading(false); }
  }, []);

  useEffect(() => { fetchActivityLog(); }, [fetchActivityLog]);

  const filteredUsers = users.filter((u) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(q) ||
      (u.employee_number || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q) ||
      (u.rfid_card || "").toLowerCase().includes(q)
    );
  });

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role?role=${newRole}`, {}, { headers: getAuthHeaders() });
      toast.success(`Role updated to ${ROLE_LABELS[newRole] || newRole}`);
      fetchUsers();
      fetchActivityLog();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update role"); }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete user ${name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeaders() });
      toast.success("User deleted");
      fetchUsers();
      fetchActivityLog();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete user"); }
  };

  const handleToggleFeature = async (feature) => {
    const updated = { ...toggles, [feature]: !toggles[feature] };
    setTogglesLoading(true);
    try {
      await axios.put(`${API}/admin/feature-toggles`, updated, { headers: getAuthHeaders() });
      setToggles(updated);
      toast.success(`${FEATURE_LABELS[feature]} ${updated[feature] ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed to update feature toggle"); }
    finally { setTogglesLoading(false); }
  };

  const openAccessDialog = (user) => {
    setAccessUser(user);
    setCustomAccess(user.custom_access || {});
  };

  const toggleUserAccess = (feature) => {
    setCustomAccess(prev => ({ ...prev, [feature]: prev[feature] === undefined ? false : !prev[feature] }));
  };

  const saveCustomAccess = async () => {
    const hasOverrides = Object.values(customAccess).some(v => v !== undefined);
    const payload = hasOverrides ? customAccess : null;
    try {
      await axios.put(`${API}/users/${accessUser.id}/access`, { custom_access: payload }, { headers: getAuthHeaders() });
      toast.success(`Access updated for ${accessUser.name}`);
      setAccessUser(null);
      fetchUsers();
    } catch { toast.error("Failed to update access"); }
  };

  const resetCustomAccess = async () => {
    try {
      await axios.put(`${API}/users/${accessUser.id}/access`, { custom_access: null }, { headers: getAuthHeaders() });
      toast.success(`Access reset to role defaults for ${accessUser.name}`);
      setAccessUser(null);
      fetchUsers();
    } catch { toast.error("Failed to reset access"); }
  };

  const handleAddUser = async () => {
    if (!addUserForm.employee_number.trim()) {
      toast.error("Employee number is required");
      return;
    }
    setAddUserLoading(true);
    try {
      await axios.post(`${API}/users`, addUserForm, { headers: getAuthHeaders() });
      toast.success(`User "${addUserForm.name || addUserForm.employee_number}" created`);
      setAddUserOpen(false);
      setAddUserForm({ name: "", employee_number: "", role: "hourly", rfid_card: "" });
      fetchUsers();
      fetchActivityLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkFile) return;
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append("file", bulkFile);
      const resp = await axios.post(`${API}/users/bulk-import`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      setBulkResult(resp.data);
      if (resp.data.created > 0) {
        toast.success(`${resp.data.created} user(s) imported`);
        fetchUsers();
        fetchActivityLog();
      }
      if (resp.data.skipped > 0) {
        toast.warning(`${resp.data.skipped} row(s) skipped`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "employee_number,name,role,rfid_card\n10050,Jane Doe,technician,RFID-SAMPLE-001\n10051,John Smith,hourly,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "user_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setEditForm({ name: user.name || "", employee_number: user.employee_number || "", rfid_card: user.rfid_card || "" });
  };

  const handleEditUser = async () => {
    if (!editForm.employee_number.trim()) {
      toast.error("Employee number is required");
      return;
    }
    setEditLoading(true);
    try {
      await axios.put(`${API}/users/${editUser.id}`, editForm, { headers: getAuthHeaders() });
      toast.success(`User "${editForm.name}" updated`);
      setEditUser(null);
      fetchUsers();
      fetchActivityLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading users...</div></div></Layout>;
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="user-management-page">
        <div className="flex items-center justify-between">
          <PageHeader title="User Management" subtitle="Manage accounts, permissions, and system features" testId="user-management-title" />
          <div className="flex gap-2">
            <Button
              onClick={() => { window.open(`${API}/users/export`, "_blank"); }}
              variant="outline"
              className="rounded-none border-[#64748B] text-[#64748B] hover:bg-[#64748B] hover:text-white flex items-center gap-2"
              data-testid="export-users-btn"
            >
              <Download size={16} />
              Export
            </Button>
            <Button
              onClick={() => { setBulkOpen(true); setBulkFile(null); setBulkResult(null); }}
              variant="outline"
              className="rounded-none border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white flex items-center gap-2"
              data-testid="bulk-import-btn"
            >
              <Upload size={16} />
              Bulk Import
            </Button>
            <Button
              onClick={() => setAddUserOpen(true)}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none flex items-center gap-2"
              data-testid="add-user-btn"
            >
              <UserPlus size={16} />
              Add User
            </Button>
          </div>
        </div>

        {users.length === 0 ? (
          <EmptyState icon={Users} message="No users found." />
        ) : (
          <div className="bg-white border border-[#E2E8F0]">
            <div className="px-6 py-3 border-b border-[#E2E8F0] bg-[#FAFAFA]">
              <div className="relative max-w-sm">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, employee #, role, or RFID..."
                  className="w-full pl-9 pr-3 py-2 border border-[#E2E8F0] text-sm bg-white focus:outline-none focus:border-[#2563EB]"
                  data-testid="user-search-input"
                />
              </div>
              {searchQuery && (
                <p className="text-xs text-[#64748B] mt-1" data-testid="user-search-count">
                  Showing {filteredUsers.length} of {users.length} users
                </p>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
                  <tr>
                    {["Name", "Employee #", "Role", "RFID", "Custom Access", "Actions"].map((h, i) => (
                      <th key={h} className={`px-6 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase ${i === 5 ? "text-right" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-[#FAFAFA] transition-colors" data-testid={`user-row-${user.id}`}>
                      <td className="px-6 py-4 text-sm text-[#0F172A] font-medium">{user.name}</td>
                      <td className="px-6 py-4 text-sm text-[#64748B] font-mono">{user.employee_number || "-"}</td>
                      <td className="px-6 py-4">
                        <Select value={user.role || "hourly"} onValueChange={(v) => handleRoleChange(user.id, v)}>
                          <SelectTrigger className="w-44 rounded-none border-[#E2E8F0] h-8 text-xs" data-testid={`role-select-${user.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white rounded-none border border-[#E2E8F0]">
                            <SelectItem value="admin">Administrator/IT</SelectItem>
                            <SelectItem value="superintendent">Superintendent</SelectItem>
                            <SelectItem value="technician">Technician</SelectItem>
                            <SelectItem value="hourly">Hourly</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-[#64748B]">{user.rfid_card || "-"}</td>
                      <td className="px-6 py-4">
                        {user.custom_access ? (
                          <span className="text-xs px-2 py-1 bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B]">Customized</span>
                        ) : (
                          <span className="text-xs text-[#94A3B8]">Role default</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(user)}
                          className="rounded-none border-[#64748B] text-[#64748B] hover:bg-[#64748B] hover:text-white"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAccessDialog(user)}
                          className="rounded-none border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white"
                          data-testid={`access-user-${user.id}`}
                        >
                          <Settings2 size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(user.id, user.name)}
                          className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Per-User Access Dialog */}
        <Dialog open={!!accessUser} onOpenChange={(open) => { if (!open) setAccessUser(null); }}>
          <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-[#0F172A]">
                Customize Access — {accessUser?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="text-xs text-[#64748B] mb-2">
              Employee #{accessUser?.employee_number} &middot; Role: {ROLE_LABELS[accessUser?.role] || accessUser?.role}
            </div>
            <p className="text-xs text-[#94A3B8] mb-4">
              Override role defaults. Green = enabled, gray = disabled. Leave untouched to use role default.
            </p>
            <div className="space-y-2">
              {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                const isEnabled = customAccess[key] !== undefined ? customAccess[key] : true;
                const isOverridden = customAccess[key] !== undefined;
                return (
                  <button
                    key={key}
                    onClick={() => toggleUserAccess(key)}
                    className={`w-full flex items-center justify-between p-3 border text-sm transition-colors ${
                      isEnabled
                        ? "border-[#10B981] bg-[#F0FDF4] text-[#0F172A]"
                        : "border-[#E2E8F0] bg-[#FAFAFA] text-[#94A3B8]"
                    }`}
                    data-testid={`user-access-toggle-${key}`}
                  >
                    <span className="flex items-center gap-2">
                      {isEnabled ? <ToggleRight size={18} className="text-[#10B981]" /> : <ToggleLeft size={18} className="text-[#CBD5E1]" />}
                      <span className="font-medium">{label}</span>
                    </span>
                    {isOverridden && <span className="text-[10px] px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B]">Override</span>}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={saveCustomAccess} className="flex-1 bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="save-access-btn">
                Save Access
              </Button>
              <Button onClick={resetCustomAccess} variant="outline" className="flex-1 rounded-none border-[#E2E8F0]" data-testid="reset-access-btn">
                Reset to Default
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add User Dialog */}
        <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
          <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md" data-testid="add-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-[#0F172A] flex items-center gap-2">
                <UserPlus size={18} /> Add New User
              </DialogTitle>
              <DialogDescription className="text-xs text-[#64748B]">
                Create a new user account with employee number, role, and optional RFID badge.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Employee Number *</Label>
                <Input
                  value={addUserForm.employee_number}
                  onChange={(e) => setAddUserForm(f => ({ ...f, employee_number: e.target.value }))}
                  placeholder="e.g. 10042"
                  className="rounded-none border-[#E2E8F0] mt-1 font-mono"
                  data-testid="add-user-employee-number"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Full Name</Label>
                <Input
                  value={addUserForm.name}
                  onChange={(e) => setAddUserForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. John Smith"
                  className="rounded-none border-[#E2E8F0] mt-1"
                  data-testid="add-user-name"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Role</Label>
                <Select value={addUserForm.role} onValueChange={(v) => setAddUserForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger className="rounded-none border-[#E2E8F0] mt-1" data-testid="add-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-none border border-[#E2E8F0]">
                    <SelectItem value="admin">Administrator/IT</SelectItem>
                    <SelectItem value="superintendent">Superintendent</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="hourly">Hourly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">RFID Card (Optional)</Label>
                <Input
                  value={addUserForm.rfid_card}
                  onChange={(e) => setAddUserForm(f => ({ ...f, rfid_card: e.target.value }))}
                  placeholder="Scan or type RFID"
                  className="rounded-none border-[#E2E8F0] mt-1 font-mono"
                  data-testid="add-user-rfid"
                />
              </div>
              <Button
                onClick={handleAddUser}
                disabled={addUserLoading || !addUserForm.employee_number.trim()}
                className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none"
                data-testid="add-user-submit-btn"
              >
                {addUserLoading ? "Creating..." : "Create User"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
          <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md" data-testid="edit-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-[#0F172A] flex items-center gap-2">
                <Pencil size={18} /> Edit User
              </DialogTitle>
              <DialogDescription className="text-xs text-[#64748B]">
                Update user details. Role can be changed via the dropdown in the table.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Employee Number *</Label>
                <Input
                  value={editForm.employee_number}
                  onChange={(e) => setEditForm(f => ({ ...f, employee_number: e.target.value }))}
                  className="rounded-none border-[#E2E8F0] mt-1 font-mono"
                  data-testid="edit-user-employee-number"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Full Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                  className="rounded-none border-[#E2E8F0] mt-1"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">RFID Card</Label>
                <Input
                  value={editForm.rfid_card}
                  onChange={(e) => setEditForm(f => ({ ...f, rfid_card: e.target.value }))}
                  placeholder="Scan or type RFID"
                  className="rounded-none border-[#E2E8F0] mt-1 font-mono"
                  data-testid="edit-user-rfid"
                />
              </div>
              <Button
                onClick={handleEditUser}
                disabled={editLoading || !editForm.employee_number.trim()}
                className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none"
                data-testid="edit-user-submit-btn"
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-lg" data-testid="bulk-import-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-[#0F172A] flex items-center gap-2">
                <Upload size={18} /> Bulk Import Users
              </DialogTitle>
              <DialogDescription className="text-xs text-[#64748B]">
                Upload a CSV file to create multiple users at once. Duplicates will be skipped.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4">
                <p className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">CSV Format</p>
                <p className="text-xs text-[#64748B] mb-2">Required column: <code className="bg-[#E2E8F0] px-1 py-0.5 font-mono">employee_number</code></p>
                <p className="text-xs text-[#64748B] mb-3">Optional columns: <code className="bg-[#E2E8F0] px-1 py-0.5 font-mono">name</code>, <code className="bg-[#E2E8F0] px-1 py-0.5 font-mono">role</code>, <code className="bg-[#E2E8F0] px-1 py-0.5 font-mono">rfid_card</code></p>
                <p className="text-xs text-[#64748B] mb-1">Valid roles: admin, superintendent, technician, hourly (defaults to hourly)</p>
                <Button variant="outline" size="sm" onClick={downloadTemplate} className="rounded-none border-[#2563EB] text-[#2563EB] text-xs mt-2" data-testid="download-template-btn">
                  <Download size={14} className="mr-1" /> Download Template
                </Button>
              </div>

              <div>
                <Label className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Select CSV File</Label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }}
                  className="block w-full mt-1 text-sm text-[#64748B] file:mr-4 file:py-2 file:px-4 file:border file:border-[#E2E8F0] file:text-sm file:font-medium file:bg-[#F8FAFC] file:text-[#0F172A] file:rounded-none file:cursor-pointer hover:file:bg-[#E2E8F0]"
                  data-testid="bulk-import-file-input"
                />
                {bulkFile && (
                  <p className="text-xs text-[#64748B] mt-1 flex items-center gap-1">
                    <FileText size={12} /> {bulkFile.name}
                  </p>
                )}
              </div>

              <Button
                onClick={handleBulkImport}
                disabled={bulkLoading || !bulkFile}
                className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none"
                data-testid="bulk-import-submit-btn"
              >
                {bulkLoading ? "Importing..." : "Import Users"}
              </Button>

              {bulkResult && (
                <div className="border border-[#E2E8F0] p-4 space-y-2" data-testid="bulk-import-result">
                  <div className="flex gap-4 text-sm">
                    <span className="text-[#10B981] font-bold">{bulkResult.created} created</span>
                    <span className="text-[#F59E0B] font-bold">{bulkResult.skipped} skipped</span>
                  </div>
                  {bulkResult.errors?.length > 0 && (
                    <div className="max-h-32 overflow-y-auto">
                      {bulkResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-[#DC2626]">
                          Row {err.row}: {err.reason}{err.employee_number ? ` (${err.employee_number})` : ""}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Global Feature Toggles */}
        <div className="bg-white border border-[#E2E8F0] p-6" data-testid="feature-toggles-panel">
          <h3 className="font-bold text-sm text-[#0F172A] mb-4 flex items-center gap-2">
            <ToggleLeft size={16} /> Global Feature Toggles
          </h3>
          <p className="text-xs text-[#64748B] mb-4">Enable or disable system modules for all users. Individual user overrides (above) take priority.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(FEATURE_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => handleToggleFeature(key)}
                disabled={togglesLoading}
                className={`flex items-center gap-2 p-3 border text-sm transition-colors ${
                  toggles[key] !== false
                    ? "border-[#10B981] bg-[#F0FDF4] text-[#0F172A]"
                    : "border-[#E2E8F0] bg-[#FAFAFA] text-[#94A3B8]"
                }`}
                data-testid={`feature-toggle-${key}`}
              >
                {toggles[key] !== false ? <ToggleRight size={18} className="text-[#10B981]" /> : <ToggleLeft size={18} className="text-[#CBD5E1]" />}
                <span className="font-medium">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* User Activity Log */}
        <div className="bg-white border border-[#E2E8F0]" data-testid="user-activity-log">
          <div className="px-6 py-4 border-b border-[#E2E8F0] bg-[#FAFAFA] flex items-center justify-between">
            <h3 className="font-bold text-sm text-[#0F172A] flex items-center gap-2">
              <History size={16} /> User Activity Log
            </h3>
            <span className="text-xs text-[#64748B]">{activityLog.length} recent entries</span>
          </div>
          {logLoading ? (
            <div className="p-6 text-center text-sm text-[#64748B]">Loading activity...</div>
          ) : activityLog.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#94A3B8]">No user activity recorded yet.</div>
          ) : (
            <div className="divide-y divide-[#E2E8F0] max-h-64 overflow-y-auto">
              {activityLog.map((entry, i) => (
                <div key={i} className="px-6 py-3 flex items-center justify-between text-sm hover:bg-[#FAFAFA] transition-colors" data-testid={`activity-entry-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] px-2 py-0.5 font-bold tracking-wider uppercase border ${
                      entry.action.includes("deleted") ? "border-[#DC2626] text-[#DC2626] bg-[#FEF2F2]" :
                      entry.action.includes("created") || entry.action.includes("imported") || entry.action.includes("registered") ? "border-[#10B981] text-[#10B981] bg-[#F0FDF4]" :
                      "border-[#F59E0B] text-[#92400E] bg-[#FEF3C7]"
                    }`}>{entry.action.replace(/_/g, " ")}</span>
                    <span className="text-[#64748B] text-xs font-mono">
                      {entry.details?.employee_number || entry.details?.target_user_id?.slice(0, 8) || entry.user_id?.slice(0, 8) || "—"}
                    </span>
                    {entry.details?.new_role && <span className="text-xs text-[#64748B]">→ {entry.details.new_role}</span>}
                    {entry.details?.changes && <span className="text-xs text-[#64748B]">{Object.keys(entry.details.changes).join(", ")}</span>}
                    {entry.details?.created != null && <span className="text-xs text-[#10B981]">{entry.details.created} created</span>}
                  </div>
                  <span className="text-xs text-[#94A3B8] font-mono whitespace-nowrap">
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Role Permissions Reference */}
        <div className="bg-[#F0F9FF] border border-[#BAE6FD] p-4">
          <h3 className="font-bold text-sm text-[#0369A1] mb-1 flex items-center gap-2">
            <Shield size={14} /> Role Permissions
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2 text-sm text-[#0C4A6E]">
            <div>
              <strong>Administrator/IT</strong>
              <ul className="mt-1 text-xs space-y-0.5">
                <li>Total system access</li>
                <li>User management &amp; access control</li>
                <li>Feature toggle control</li>
                <li>Database backup/restore</li>
                <li>Audit log access</li>
              </ul>
            </div>
            <div>
              <strong>Superintendent</strong>
              <ul className="mt-1 text-xs space-y-0.5">
                <li>All Technician permissions</li>
                <li>Delete data</li>
                <li>Audit log access</li>
                <li>Overdue maintenance checks</li>
                <li>Manage scheduled scan paths</li>
              </ul>
            </div>
            <div>
              <strong>Technician</strong>
              <ul className="mt-1 text-xs space-y-0.5">
                <li>All Hourly permissions</li>
                <li>Upload blueprints</li>
                <li>Create/edit records</li>
                <li>Run network scans</li>
                <li>Parts catalog management</li>
              </ul>
            </div>
            <div>
              <strong>Hourly</strong>
              <ul className="mt-1 text-xs space-y-0.5">
                <li>Search &amp; view records only</li>
                <li>Print blueprints &amp; reports</li>
                <li>QR Scanner access</li>
                <li>No create/edit/delete access</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
